<template>
  <Modal
    v-show="showModal"
    class="scoped"
    content-class="column no-wrap"
    :full-height="showArticle"
    header-class
    :loading="loading || loadingMediaObject"
    :modal-style="modalStyle"
    offset-menu
    :title="title"
    @close="$emit('close')"
  >
    <template
      v-if="paperArticle"
      #header
    >
      <div class="bold bigger2 q-pa-md q-pr-xl">
        {{ title }}
      </div>
      <Tabs
        v-if="showTabs"
        v-model="currentTab"
      >
        <Tab
          :label="$t('modals.paper_preview_modal.tab_text')"
          name="text"
        />
        <template v-if="localMediaObjects.length">
          <Tab
            v-for="mediaObject in localMediaObjects"
            :key="mediaObject.id"
            :label="mediaObject.buttonLabel"
            :name="`pdf-${mediaObject.id}`"
            @click="tabClicked(mediaObject)"
          />
        </template>
        <Tab
          v-else
          :label="$t('modals.paper_preview_modal.tab_pdf')"
          name="pdf"
        />
      </Tabs>
    </template>
    <template v-if="showArticle">
      <div
        v-if="currentTab === 'text'"
        class="column no-wrap overflow-hidden"
      >
        <div class="row q-col-gutter-md q-pa-lg">
          <div class="col-4">
            <div class="field-heading q-mb-xs">
              {{ $t("modals.paper_preview_modal.date_heading") }}
            </div>
            <div>
              {{ dateInTimeZone }}
            </div>
          </div>

          <div class="col-4">
            <div class="field-heading q-mb-xs">
              {{ $t("modals.paper_preview_modal.publication_heading") }}
            </div>
            <div>{{ paperArticle.source.name }}</div>
          </div>

          <template v-if="author">
            <div class="col-4">
              <div class="field-heading q-mb-xs">
                {{ $t("modals.paper_preview_modal.author_heading") }}
              </div>
              <div>
                {{ capitalizeName(author) }}
              </div>
            </div>
          </template>

          <div class="col-4">
            <div class="field-heading q-mb-xs">
              {{ $t("modals.paper_preview_modal.word_count_heading") }}
            </div>
            <div>
              {{ paperArticle.word_count }}
            </div>
          </div>

          <div
            v-if="paperArticle.page_numbers"
            class="col-4"
          >
            <div class="field-heading q-mb-xs">
              {{
                $tc(
                  "modals.paper_preview_modal.page_heading",
                  pageNumbers.length
                )
              }}
            </div>
            <div>
              {{ toSentence(pageNumbers) }}
            </div>
          </div>

          <div
            v-if="paperArticle.size"
            class="col-4"
          >
            <div class="field-heading q-mb-xs">
              {{ $t("modals.paper_preview_modal.size_heading") }}
            </div>
            <div>{{ paperArticle.size }} cm<sup>2</sup></div>
          </div>
        </div>

        <BaseSeparator />

        <!-- eslint-disable vue/no-v-html -->
        <div
          class="q-pa-lg highlightable flex-1 overflow-auto"
          v-html="highlightedText"
        />
      </div>
      <div
        v-else
        class="column no-wrap flex-1"
      >
        <PdfViewer
          v-if="mediaObjects.length"
          data-testid="media-object"
          fallback-class="tw:my-4"
          :loading="loadingMediaObject"
          object-class="no-border"
          :pdf-url="asyncPdfUrl"
        />
        <PdfViewer
          v-else
          :pdf-url="paperArticle.pdf_url"
        />
      </div>

      <MentionLegalDisclaimer
        class="q-px-lg q-py-md"
        :mention="paperArticle"
        separator
      />
    </template>
    <div
      v-else
      class="q-pa-md row justify-end"
    >
      <BaseButton
        color="primary"
        :label="$t('modals.paper_preview_modal.afr_button')"
        new-design
        @click="openExternalUrl(paperArticle.url)"
      />
    </div>
  </Modal>
</template>

<script>
import { utcToZonedTime } from "date-fns-tz";
import { storeToRefs } from "pinia";
import { Platform } from "quasar";

import { BaseButton, BaseSeparator, Tab, Tabs } from "shared/components/base";
import Modal from "shared/components/core/modals/Modal.vue";
import MentionLegalDisclaimer from "shared/components/MentionLegalDisclaimer.vue";
import PdfViewer from "shared/components/PdfViewer.vue";
import { toSentence } from "shared/helpers/array";
import { formatIntlDate } from "shared/helpers/date";
import { openExternalUrl } from "shared/helpers/external";
import { capitalizeName, highlight, nl2br } from "shared/helpers/string";
import UcpMediaObject from "shared/resources/UcpMediaObject";
import { useGlobalStore } from "shared/stores/global";
import useUserStore from "shared/stores/user";

export default {
  name: "PaperPreviewModal",
  components: {
    PdfViewer,
    Modal,
    BaseButton,
    BaseSeparator,
    Tab,
    Tabs,
    MentionLegalDisclaimer,
  },
  props: {
    mention: {
      type: Object,
      default: () => ({}),
    },
    keywords: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["close"],
  setup() {
    const globalStore = useGlobalStore();
    const { token } = useUserStore();
    const { isStreem } = storeToRefs(globalStore);

    return {
      token,
      isStreem,
    };
  },
  data() {
    return {
      currentTab: "text",
      paperArticle: null,
      loading: false,
      loadingMediaObject: false,
      asyncPdfUrl: null,
      mediaObjects: [],
      showModal: false,
    };
  },
  computed: {
    showArticle() {
      return this.paperArticle && this.paperArticle.permission !== false;
    },
    modalStyle() {
      return this.showArticle ? { maxWidth: "900px" } : {};
    },
    showTabs() {
      if (!this.showArticle) {
        return false;
      }

      return this.mediaObjects.length || this.isStreem;
    },
    author() {
      return this.paperArticle?.author || "";
    },
    title() {
      if (!this.paperArticle) {
        return "";
      }

      if (!this.showArticle) {
        return this.$t("modals.paper_preview_modal.unauthorised_title");
      }

      let { title } = this.paperArticle;

      if (this.paperArticle.translated_title) {
        title = `${this.paperArticle.translated_title} (${title})`;
      }

      return title;
    },
    highlightedText() {
      if (!this.paperArticle) {
        return "";
      }

      let { body } = this.paperArticle;

      if (this.paperArticle.translated_body) {
        body = `${this.$t("modals.paper_preview_modal.translation")}: ${
          this.paperArticle.translated_body
        }`;
      }

      return body && nl2br(highlight(body, this.keywords));
    },
    dateInTimeZone() {
      if (!this.paperArticle.published_at) {
        return "";
      }

      return formatIntlDate(
        utcToZonedTime(
          new Date(this.paperArticle.published_at),
          this.paperArticle.source.time_zone
        ),
        { dateStyle: "medium" }
      );
    },
    localMediaObjects() {
      return this.mediaObjects.map((mediaObject, mediaObjectIndex) => {
        let buttonLabel = "PDF";

        if (this.mediaObjects.length > 1) {
          buttonLabel += ` - ${mediaObjectIndex + 1}`;
        }

        return {
          ...mediaObject,
          loading: this.loadingMediaObject === mediaObject.id,
          buttonLabel,
        };
      });
    },
    pageNumbers() {
      return this.paperArticle?.page_numbers || [];
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    capitalizeName,
    toSentence,
    openExternalUrl,
    async load() {
      try {
        this.loading = true;
        await this.loadPaperArticle();
        this.showModal = true;

        if (!this.showArticle) {
          return;
        }

        if (!this.paperArticle.body) {
          openExternalUrl(this.paperArticle.authenticated_url);

          this.$nextTick(() => {
            this.$emit("close");
          });

          return;
        }

        await this.loadMediaObjects();
      } finally {
        this.loading = false;
      }
    },
    async loadPaperArticle() {
      try {
        this.paperArticle = (
          await this.$streemApiV1.get(
            `${this.mention.type}s/${this.mention.id}`
          )
        ).data;
      } catch (error) {
        this.$error(this.$t("modals.paper_preview_modal.load_error"), error);
      }
    },
    async loadMediaObjects() {
      try {
        this.mediaObjects = (
          await UcpMediaObject.where({ mediaId: this.mention.id }).all()
        ).data;
      } catch {
        // do nothing
      }
    },
    tabClicked(mediaObject) {
      this.loadMediaObject(mediaObject.id);
    },
    async loadMediaObject(id) {
      try {
        this.loadingMediaObject = true;
        const isAndroid = Boolean(Platform.is.android);

        const { data, headers, request } = await this.$streemApiV1.get(
          `ucp_media_objects/${id}/attachment`,
          // TODO: to be removed
          ...(isAndroid ? { params: { auth_token: this.token } } : {})
        );

        if (isAndroid) {
          this.asyncPdfUrl = request.responseURL;

          return;
        }

        const blob = new Blob([data], {
          type: headers["content-type"],
        });

        this.asyncPdfUrl = URL.createObjectURL(blob);
      } catch (error) {
        this.$error(
          this.$t("modals.paper_preview_modal.download_error"),
          error
        );
      } finally {
        this.loadingMediaObject = false;
      }
    },
  },
};
</script>
